import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import DashboardLayout from '@/components/feature/DashboardLayout';
import { api, getApiErrorMessage, ApiError } from '@/services/api';
import { useToast } from '@/hooks/useToast';
import { useTheme } from '@/hooks/useTheme';
import ContractsRedesign from './ContractsRedesign';

interface Contract {
  id: string;
  contractNumber: string;
  contractType: string;
  counterpartyName: string;
  assetName?: string;
  startDate: string;
  endDate: string;
  status: string;
}

/**
 * Dual mode (handoff section 6): the stand with VITE_REDESIGN shows the
 * redesign, prod keeps ContractsPageLegacy below. /contracts has no feature
 * gate, so prod does render this route.
 */
export default function ContractsPage() {
  return import.meta.env.VITE_REDESIGN === '1' ? <ContractsRedesign /> : <ContractsPageLegacy />;
}

function ContractsPageLegacy() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { addToast } = useToast();
  const { isDark } = useTheme();
  const [items, setItems] = useState<Contract[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const cardBg = isDark ? '#1A1A1A' : '#fff';
  const cardBorder = isDark ? 'rgba(255,255,255,0.07)' : '#E5E5E8';
  const textMain = isDark ? '#E0E0E0' : '#1A1A1A';
  const textMuted = isDark ? '#6B6B70' : '#6B6B70';
  const inputBg = isDark ? 'rgba(255,255,255,0.05)' : '#F5F5F7';
  const inputBorder = isDark ? 'rgba(255,255,255,0.1)' : '#E5E5E8';
  const tableHeaderBg = isDark ? 'rgba(255,255,255,0.03)' : '#F5F5F7';
  const tableBorder = isDark ? 'rgba(255,255,255,0.05)' : '#F5F5F7';
  const separator = isDark ? 'rgba(255,255,255,0.07)' : '#E5E5E8';
  const hoverBg = isDark ? 'rgba(255,255,255,0.04)' : '#F9F9FB';
  const iconBg = isDark ? 'rgba(255,255,255,0.05)' : '#F5F5F7';
  const selectBg = isDark ? '#1A1A1A' : '#fff';

  useEffect(() => {
    loadContracts();
  }, []);

  const loadContracts = async () => {
    setLoading(true);
    try {
      const data = await api.get<Contract[]>('/contracts');
      setItems(data);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        setItems([]);
      } else {
        const message = getApiErrorMessage(err, 'Failed to load contracts');
        addToast(message, 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const filtered = items.filter((c) =>
    c.contractNumber.toLowerCase().includes(search.toLowerCase()) ||
    c.counterpartyName.toLowerCase().includes(search.toLowerCase())
  );

  const getStatusBadge = (status: string) => {
    const map: Record<string, { bg: string; color: string }> = {
      active: { bg: 'rgba(13,122,60,0.1)', color: '#0D7A3C' },
      draft: { bg: 'rgba(107,107,112,0.1)', color: '#6B6B70' },
      pending: { bg: 'rgba(184,134,11,0.1)', color: '#B8860B' },
      expired: { bg: 'var(--surface-2)', color: 'var(--accent)' },
      terminated: { bg: 'rgba(107,107,112,0.1)', color: '#6B6B70' },
    };
    const style = map[status] ?? map.draft;
    return (
      <span className="text-xs font-medium px-2.5 py-1 rounded-full capitalize" style={{ background: style.bg, color: style.color }}>
        {status}
      </span>
    );
  };

  return (
    <DashboardLayout breadcrumb={[t('contracts_title')]}>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: textMain, fontFamily: 'Inter, sans-serif' }}>
            {t('contracts_title')}
          </h1>
          <p className="text-sm mt-1" style={{ color: textMuted }}>
            {t('contracts_subtitle')}
          </p>
        </div>
      </div>

      <div className="rounded-xl" style={{ background: cardBg, border: `1px solid ${cardBorder}` }}>
        <div className="flex items-center gap-3 p-4" style={{ borderBottom: `1px solid ${separator}` }}>
          <div className="flex items-center gap-2 px-3 py-2 rounded-md flex-1 max-w-md" style={{ background: inputBg, border: `1px solid ${inputBorder}` }}>
            <i className="ri-search-line text-sm" style={{ color: textMuted }}></i>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('contracts_search')}
              className="bg-transparent outline-none text-sm flex-1"
              style={{ color: textMain }}
            />
          </div>
          <button
            onClick={loadContracts}
            className="w-9 h-9 flex items-center justify-center rounded-md cursor-pointer transition-colors"
            style={{ border: `1px solid ${inputBorder}`, background: selectBg }}
            title="Refresh"
          >
            <i className="ri-refresh-line text-sm" style={{ color: textMuted }}></i>
          </button>
        </div>

        {loading ? (
          <div className="p-12 text-center">
            <i className="ri-loader-4-line animate-spin text-2xl" style={{ color: textMuted }}></i>
            <p className="text-sm mt-3" style={{ color: textMuted }}>{t('contracts_loading')}</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center">
            <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3" style={{ background: iconBg }}>
              <i className="ri-file-list-3-line text-xl" style={{ color: textMuted }}></i>
            </div>
            <p className="text-sm font-medium" style={{ color: textMain }}>{t('contracts_not_found')}</p>
            <p className="text-xs mt-1" style={{ color: textMuted }}>
              {search ? t('contracts_filter_hint') : t('contracts_not_found_hint')}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr style={{ background: tableHeaderBg }}>
                  <th className="text-left px-4 py-3 font-semibold text-xs uppercase tracking-wider" style={{ color: textMuted }}>{t('contracts_col_number')}</th>
                  <th className="text-left px-4 py-3 font-semibold text-xs uppercase tracking-wider" style={{ color: textMuted }}>{t('contracts_col_type')}</th>
                  <th className="text-left px-4 py-3 font-semibold text-xs uppercase tracking-wider" style={{ color: textMuted }}>{t('contracts_col_counterparty')}</th>
                  <th className="text-left px-4 py-3 font-semibold text-xs uppercase tracking-wider" style={{ color: textMuted }}>{t('contracts_col_asset')}</th>
                  <th className="text-left px-4 py-3 font-semibold text-xs uppercase tracking-wider" style={{ color: textMuted }}>{t('contracts_col_period')}</th>
                  <th className="text-left px-4 py-3 font-semibold text-xs uppercase tracking-wider" style={{ color: textMuted }}>{t('contracts_col_status')}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c) => (
                  <tr key={c.id} style={{ borderBottom: `1px solid ${tableBorder}` }}>
                    <td className="px-4 py-3 font-medium" style={{ color: textMain }}>{c.contractNumber}</td>
                    <td className="px-4 py-3 capitalize" style={{ color: textMuted }}>{c.contractType.replace('_', ' ')}</td>
                    <td className="px-4 py-3" style={{ color: textMain }}>{c.counterpartyName}</td>
                    <td className="px-4 py-3 text-xs" style={{ color: textMuted }}>{c.assetName ?? '—'}</td>
                    <td className="px-4 py-3 text-xs" style={{ color: textMuted }}>
                      {new Date(c.startDate).toLocaleDateString('uk-UA')} — {new Date(c.endDate).toLocaleDateString('uk-UA')}
                    </td>
                    <td className="px-4 py-3">{getStatusBadge(c.status)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}